// src/lib/auth.ts

import { jwtDecode } from 'jwt-decode';
import Router from 'next/router';
import PREFIX_URL from './vars';

// Types
interface AuthTokens {
  access: string;
  refresh: string;
}

interface DecodedToken {
  exp: number;
  user_id: string;
  // Add other fields from your JWT payload
}

// Local storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// API endpoints (adjust to match your Django backend)
const LOGIN_URL = '/api/token/';
const REFRESH_URL = '/api/token/refresh/';

// Axios instance with interceptors
import axios from 'axios';

const api = axios.create({
  baseURL: PREFIX_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

console.log(api.getUri);

// Add request interceptor to include token in requests
api.interceptors.request.use(
  async (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is not 401 or request has already been retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    originalRequest._retry = true;
    
    try {
      // Try to refresh the access token
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${getAccessToken()}`;
        return api(originalRequest);
      } else {
        // If refresh failed, logout and redirect to login
        logout();
        Router.push('/login');
        return Promise.reject(error);
      }
    } catch (refreshError) {
      // If refresh fails, logout and redirect to login
      logout();
      Router.push('/login');
      return Promise.reject(refreshError);
    }
  }
);

// Authentication functions
export const login = async (username: string, password: string): Promise<boolean> => {
  try {
    const response = await api.post<AuthTokens>(LOGIN_URL, { 
      username, 
      password 
    });
    
    // Store tokens
    setTokens(response.data.access, response.data.refresh);
    return true;
  } catch (error) {
    console.error('Login failed:', error);
    return false;
  }
};

export const logout = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  if (!token) return false;
  
  // Check if token is expired
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Return true if token is not expired
    return decoded.exp > currentTime;
  } catch (error) {
    return false;
  }
};

// Token helpers
const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const refreshAccessToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  
  try {
    const response = await axios.post<{ access: string }>(
      `${PREFIX_URL}${REFRESH_URL}`,
      { refresh: refreshToken }
    );
    
    // Store new access token
    localStorage.setItem(ACCESS_TOKEN_KEY, response.data.access);
    return true;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return false;
  }
};

// Export API instance for use in components/pages
export { api };